import { supabase } from '../supabase'
import type { TrophyRow } from '../database.types'

export type Trophy = TrophyRow

export interface ProfileStats {
  games_played: number
  weekly_events_entered: number
  votes_received: number
}

export interface ProfileActivity {
  occurred_at: string
  kind:
    | 'iet_write' | 'iet_play'
    | 'colorful_pick' | 'colorful_play'
    | 'tokyo_metro_play'
    | 'three_step_write' | 'three_step_play'
    | 'fake_out_play' | 'close_enough_play'
  game_id: 'ideatthat' | 'colorful' | 'tokyo-metro' | 'three-step'
         | 'fake-out' | 'close-enough'
  title: string
  subtitle: string
}

const ZERO_STATS: ProfileStats = {
  games_played: 0,
  weekly_events_entered: 0,
  votes_received: 0,
}

/**
 * Public-safe fields about any user. Used by PublicProfileScreen
 * when someone taps an author name on the weekly voting/winners
 * surfaces. Anyone signed in can read this — the `profiles` table
 * has `profiles_read` allowing all authenticated reads, by design,
 * so usernames/avatars aren't a leak.
 */
export interface PublicProfileBasics {
  id: string
  display_name: string
  username: string | null
  avatar_url: string | null
}

export async function fetchPublicProfile(profileId: string): Promise<PublicProfileBasics | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, display_name, username, avatar_url')
    .eq('id', profileId)
    .maybeSingle()
  if (error) throw error
  return (data as PublicProfileBasics | null) ?? null
}

export async function fetchProfileStats(profileId: string): Promise<ProfileStats> {
  const { data, error } = await supabase.rpc('get_profile_stats', {
    p_profile_id: profileId,
  })
  if (error) throw error
  // Postgres `returns table` shows up as a single-row array.
  const row = data?.[0]
  if (!row) return ZERO_STATS
  return {
    games_played:          Number(row.games_played ?? 0),
    weekly_events_entered: Number(row.weekly_events_entered ?? 0),
    votes_received:        Number(row.votes_received ?? 0),
  }
}

export async function fetchProfileActivity(
  profileId: string, limit = 10,
): Promise<ProfileActivity[]> {
  const { data, error } = await supabase.rpc('get_profile_recent_activity', {
    p_profile_id: profileId,
    p_limit:      limit,
  })
  if (error) throw error
  return (data ?? []) as ProfileActivity[]
}

/**
 * Trophies the profile has earned. Today: one per IET weekly win,
 * with the week numbered Week 1, Week 2, … in chronological order
 * across all finalised events.
 */
/**
 * Report a finished LOCAL game (solo vs AI, pass-play) so the lifetime
 * Games Played counter covers games that never touch the server.
 * Online room games are counted by database triggers when the room
 * finishes — never call this for those or the game counts twice.
 * Fire-and-forget: an offline miss just loses one from a vanity stat.
 */
export function bumpMyGamesPlayed(): void {
  // RPC not in the generated DB types yet — same untyped-client
  // pattern as user_flags / user_game_notes.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(supabase as any).rpc('bump_my_games_played').then(
    () => {},
    () => { /* offline — the counter misses one */ },
  )
}

export interface StampsPanel {
  weekly_stamps: number
  colorful_done: boolean
  iet_done: boolean
  three_step_done: boolean
  bonus_awarded: boolean
  /** Qualifying weekly trophy counts (at least two humans played). */
  iet_trophies: number
  colorful_trophies: number
  three_step_trophies: number
  iet5_done: boolean
  colorful5_done: boolean
  three_step5_done: boolean
  fake_out_trophies: number
  close_enough_trophies: number
  fake_out5_done: boolean
  close_enough5_done: boolean
}

/**
 * Stamps COLLECTED BY PLAYING (weekly sole-win stamps + app bonuses).
 * Monotonic by design: spending happens on the website and never
 * lowers this number. The call also advances the APP STARTER BONUS
 * tracker server-side, so simply opening the profile after finishing
 * the third game is what mints the bonus.
 */
export async function fetchMyStampsPanel(): Promise<StampsPanel> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).rpc('my_stamps_panel')
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  if (!row) throw new Error('No stamps data returned.')
  return row as StampsPanel
}

export async function fetchMyTrophies(profileId: string): Promise<Trophy[]> {
  const { data, error } = await supabase.rpc('list_my_trophies', {
    p_profile_id: profileId,
  })
  if (error) throw error
  return (data ?? []) as Trophy[]
}

/**
 * Resize an arbitrary image File to a 600×600 cover-cropped WebP and
 * upload it to the user's avatar slot in Supabase Storage. Returns the
 * cache-busted public URL ready to set on profile.avatar_url.
 *
 * Cover-crop: scale the source so its short side fills 600px, then
 * centre-crop the long side. Avoids letterboxing and matches how the
 * Avatar component renders (`object-cover`).
 */
export async function uploadProfileAvatar(
  userId: string, file: File,
): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('That file does not look like an image.')
  }
  const blob = await resizeToWebp(file, 600, 0.85)
  const path = `${userId}/avatar.webp`
  const { error: uploadErr } = await supabase.storage
    .from('avatars')
    .upload(path, blob, {
      contentType: 'image/webp',
      upsert: true,
      cacheControl: 'public, max-age=60',
    })
  if (uploadErr) throw uploadErr

  const { data: pub } = supabase.storage.from('avatars').getPublicUrl(path)
  // Bust the CDN cache so the new image shows immediately rather than
  // serving the stale upload from before.
  return `${pub.publicUrl}?v=${Date.now()}`
}

async function resizeToWebp(file: File, size: number, quality: number): Promise<Blob> {
  const url = URL.createObjectURL(file)
  try {
    const img = await loadImage(url)
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas 2D not supported in this browser.')

    // Cover-crop: scale so the source's short side fills `size`,
    // then centre-crop the longer side.
    const scale = Math.max(size / img.naturalWidth, size / img.naturalHeight)
    const drawW = img.naturalWidth * scale
    const drawH = img.naturalHeight * scale
    const dx = (size - drawW) / 2
    const dy = (size - drawH) / 2
    ctx.drawImage(img, dx, dy, drawW, drawH)

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(b => b ? resolve(b) : reject(new Error('Could not encode WebP.')),
        'image/webp', quality)
    })
  } finally {
    URL.revokeObjectURL(url)
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Could not read image file.'))
    img.src = src
  })
}
